import { useState } from "react";
import { useIdentity } from "@/features/identity/hooks/useIdentity";
import { uploadMedia, deleteMediaPath, deleteOwnMediaByUrl } from "@/features/identity/api/mediaApi";
import { MEDIA } from "@/features/identity/constants/media";
import { validateMediaFile } from "@/features/identity/validation";
import { friendlyDataError, friendlyStorageError } from "@/lib/errors";

// Order: upload new → save URL → delete old. On save failure the new object is removed.
export function useMediaUpload(kind) {
  const cfg = MEDIA[kind];
  const { profile, saveProfile } = useIdentity();
  const [state, setState] = useState({ status: "idle", message: null });
  const current = profile[cfg.column];

  const upload = async (file) => {
    const invalid = validateMediaFile(kind, file);
    if (invalid) return setState({ status: "error", message: invalid });
    setState({ status: "uploading", message: null });
    let uploaded;
    try {
      uploaded = await uploadMedia(kind, file);
    } catch (err) {
      return setState({ status: "error", message: friendlyStorageError(err) });
    }
    try {
      await saveProfile({ [cfg.column]: uploaded.url });
    } catch (err) {
      await deleteMediaPath(uploaded.path).catch(() => {});
      return setState({ status: "error", message: friendlyDataError(err, "We couldn't save your image. Please try again.") });
    }
    await deleteOwnMediaByUrl(current);
    setState({ status: "success", message: `${cfg.label} ${current ? "replaced" : "added"}.` });
  };

  const remove = async () => {
    setState({ status: "removing", message: null });
    try {
      await saveProfile({ [cfg.column]: null });
    } catch (err) {
      return setState({ status: "error", message: friendlyDataError(err, "We couldn't remove this image. Please try again.") });
    }
    await deleteOwnMediaByUrl(current);
    setState({ status: "success", message: `${cfg.label} removed.` });
  };

  const busy = state.status === "uploading" || state.status === "removing";
  return { cfg, current, ...state, busy, upload, remove };
}
